import React, { useRef, useState, memo } from 'react';
import { useSlate } from 'slate-react';
import classnames from 'classnames';
import { useClickOutside } from 'react-haiku';
import { useSpring, animated } from 'react-spring';

import Icon from 'components/Misc/Icon';

// Align's button component
// @format: string | format's type
// @icon: string | icon's type
const Align = memo(() => {
	// Store container's ref
	const ref = useRef(null);

	// Get current editor
	const editor = useSlate();

	// Check if current align is active
	const isActive = editor.isAlignActive();

	// Check if alignment is disabled
	const isDisabled = editor.isAlignDisabled();

	// Cache alignment's container visibility
	const [visible, setVisible] = useState(false);

	// Store menu's animation styles
	const style = useSpring({
		opacity: visible ? 1 : 0,
		pointerEvents: visible ? 'auto' : 'none',
		config: {
			duration: 150
		}
	});

	// Concat classnames
	const cls = classnames(
		isActive && 'bg-neutral-100 dark:bg-neutral-700',
		isDisabled
			? 'text-neutral-400 dark:text-neutral-700'
			: 'text-neutral-800 dark:text-white',
		'w-9 h-9 leading-9 rounded-lg text-xl text-center cursor-pointer transition-colors'
	);

	// Concat left alignment classnames
	const leftClassName = classnames(
		'w-9 h-9 leading-9 text-lg text-center text-neutral-800 dark:text-white transition-colors cursor-pointer'
	);

	// Concat center alignment classnames
	const centerClassName = classnames(
		isActive === 'center' && 'bg-neutral-100 dark:bg-neutral-700',
		'w-9 h-9 leading-9 text-lg text-center text-neutral-800 dark:text-white transition-colors cursor-pointer'
	);

	// Concat right alignment classnames
	const rightClassName = classnames(
		isActive === 'right' && 'bg-neutral-100 dark:bg-neutral-700',
		'w-9 h-9 leading-9 text-lg text-center text-neutral-800 dark:text-white transition-colors cursor-pointer'
	);

	// Concat justify alignment classnames
	const justifyClassName = classnames(
		isActive === 'justify' && 'bg-neutral-100 dark:bg-neutral-700',
		'w-9 h-9 leading-9 text-lg text-center text-neutral-800 dark:text-white transition-colors cursor-pointer'
	);

	// Handle align's button click
	// @type: string | alignment's type'
	const onAlign = type => {
		// Toggle selected alignment's type
		editor.toggleAlign(type);
		
		// Hide additional menu
		setVisible(false);
	};

	// Handles alignment's button click
	// Doesn't show additional menu if alignment is disabled
	const onClick = () => setVisible(v => !v);

	// Handles click outside of alignment's container
	const onOutside = () => setVisible(false);

	// Use outside handler
	useClickOutside(ref, onOutside);

	return (
		<div ref={ref}>
			{/* Formater's button */}
			<Icon
				icon="format_align_left"
				onClick={onClick}
				className={cls}
				disabled={isDisabled}
			/>

			{/* Additional menu */}
			<animated.div style={style} className="absolute z-10 w-9 top-auto translate-y-2 flex flex-col bg-white dark:bg-neutral-700 dark:border-neutral-700 shadow-md rounded-lg transition-colors">
				<Icon
					icon="format_align_left"
					onClick={() => onAlign('')}
					className={leftClassName}
				/>

				<Icon
					icon="format_align_center"
					onClick={() => onAlign('center')}
					className={centerClassName}
				/>

				<Icon
					icon="format_align_right"
					onClick={() => onAlign('right')}
					className={rightClassName}
				/>

				<Icon
					icon="format_align_justify"
					onClick={() => onAlign('justify')}
					className={justifyClassName}
				/>
			</animated.div>
		</div>
	);
});

export default Align;
